/**
 * How-to content — Operations: housekeeping, maintenance, storekeeper, gift shop,
 * pool & kids, pool attendant, kids zone, driver.
 * Format: see guides.exec.js.
 */

export const OPS_GUIDES = {
  /* ═════════════════════════ HOUSEKEEPING ═════════════════════════ */
  main_housekeeping: {
    title: 'Housekeeping',
    intro: 'See every room’s cleaning status, clean rooms with a checklist, get them approved and report anything broken.',
    faq: [{ q: 'I cannot start cleaning a room', a: 'The room may already have an active task or be Out of Order. Restore it first if it was fixed.' }],
    sections: [
      { h: 'Read the room board', d: 'Know which rooms need you.',
        steps: [
          'Open **🏨 Room Board**. Rooms are grouped by floor; use **All Floors** to filter.',
          { t: 'Read each room’s status.', tip: 'Needs Cleaning = your work. In Progress = being cleaned. Awaiting OK = waiting for inspection. Clean & Ready = can be sold. Out of Order = broken.' },
          'The tiles show how many rooms need cleaning, are awaiting OK, are clean and are done today.',
          'Click **🖨 Print Room Status Sheet** for a paper list for your team.',
        ],
      },
      { h: 'Clean a room', d: 'From dirty to ready to sell.',
        steps: [
          'Click a room marked **Needs Cleaning**.',
          { t: 'Optionally choose a person in **Assign to… (optional)**.', tip: 'Supervisors use this to share rooms between attendants.' },
          'Click **🧹 Start Cleaning**.',
          { t: 'Tick each checklist item as you finish it. The progress bar fills up.', tip: 'Do not tick ahead — supervisors check the room against the list.' },
          'When everything is done, click **✅ Submit for Inspection**.',
        ],
        result: 'The room shows ⏳ Submitted — awaiting approval.',
        mistakes: ['Submitting with items unticked.', 'Leaving a room In Progress at the end of the shift.'],
      },
      { h: 'Inspect and approve a room (supervisor / reception)', d: 'Confirm the room is truly ready.',
        steps: [
          'Open a room showing **Awaiting OK**.',
          'Walk the room and compare with the ticked checklist.',
          'Click **✅ Approve — Mark Room Clean**.',
        ],
        result: 'The room becomes Clean & Ready and reception can give it to a guest.',
      },
      { h: 'Report a broken room', d: 'AC, plumbing, furniture or anything that stops the room being sold.',
        steps: [
          'Open the room and click **⚠ Issue**.',
          { t: 'Describe the problem in **Describe the issue** (e.g. Broken AC, plumbing leak).', tip: 'Be specific: what, where, how bad.' },
          'Click **⚠️ Submit Report**.',
        ],
        result: 'The room is marked Out of Order and management is alerted, so it is not sold.',
      },
      { h: 'Bring a fixed room back', d: 'After the repair is done.',
        steps: [
          'Open the room and click **🔧 Restore Room**.',
          'Type what was done in **Resolution / Work Done** (e.g. AC repaired by technician).',
          'Click **✅ Restore Room**.',
        ],
        result: 'Out of Order is cleared and the room goes back to the cleaning queue.',
      },
      { h: 'See cleaning history', d: 'Who cleaned what and when.',
        steps: ['Open **📋 Log History**.', 'Each entry shows the room, who cleaned it, when they started and submitted, and who approved it.'],
      },
    ],
  },

  /* ═════════════════════════ MAINTENANCE ═════════════════════════ */
  maintenance: {
    title: 'Maintenance',
    intro: 'Log repairs, schedule routine maintenance and track what it costs.',
    tip: 'Critical open work orders and overdue routine tasks show on the GM Dashboard.',
    faq: [{ q: 'A task shows ⚠ OVERDUE', a: 'Its scheduled or due date has passed. Complete it or reschedule; overdue items appear on the GM Dashboard.' }],
    sections: [
      { h: 'Log a repair (work order)', d: 'Anything broken that needs fixing.',
        steps: [
          'Click **+ Work Order**.',
          { t: 'Type the **Work Order Title** (e.g. AC unit not cooling — Room 204) and a **Detailed Description**.', tip: 'Only the title and description are required — fill the rest if you know it.' },
          { t: 'Choose the **Priority**: Low, Medium, High or Critical.', tip: 'Critical = immediate (safety, flooding, a guest room unusable). High = within 24–48 hours.' },
          'Choose the **Category** (Electrical, Plumbing, HVAC / AC, Carpentry, Painting, Deep Clean, Equipment, IT / Tech, Pool, Grounds, Other).',
          'Type the **Location / Room** and choose the **Asset (optional)**.',
          'Set the **Scheduled Date**, **Est. Hours** and **Est. Cost (KES)**, then save.',
        ],
        result: 'The work order appears in Work Orders as open.',
      },
      { h: 'Work through a repair', d: 'Track progress and record costs.',
        steps: [
          'Click **▶ Start** when work begins.',
          { t: 'Click **+ Cost** to log parts, labour or contractor costs against it.', tip: 'Choose the Cost Type (👷 Labour, 🔩 Parts, 🏗️ Contractor), description, amount and supplier.' },
          'Click **✓ Complete** when it is fixed.',
        ],
        result: 'Estimated and actual cost are shown side by side.',
      },
      { h: 'Schedule routine maintenance', d: 'Generators, pool pumps, AC filters — before they fail.',
        steps: [
          'Click **+ PM Schedule**.',
          'Type the **PM Task Title** (e.g. HVAC Filter Replacement) and instructions.',
          'Choose the asset and **Assign To (optional)**.',
          'Choose the **Frequency**: Daily, Weekly, Monthly, Quarterly, Bi-Annual or Annual.',
          'Set the **Next Due Date** and **Est. Cost per Run (KES)**, then save.',
          { t: 'Each time it is done, click **✓ Mark Done**.', tip: 'The next due date moves forward automatically.' },
        ],
        mistakes: ['Not clicking Mark Done — the task shows as overdue on the GM Dashboard.'],
      },
      { h: 'Track maintenance costs', d: 'What maintenance is costing you.',
        steps: [
          'Click **+ Record Cost**, choose the **Work Order**, the cost type, description, amount and supplier.',
          'Open **💰 Cost Tracking** for all costs and **📊 Summary** for work orders by status.',
        ],
      },
    ],
  },

  /* ═════════════════════════ STOREKEEPER ═════════════════════════ */
  main_storekeeper: {
    title: 'Storekeeper',
    intro: 'Keep the stores accurate: add items, receive goods, issue to departments, count stock and order what is running low.',
    faq: [
      { q: 'I cannot print the shopping list', a: 'Printing unlocks only after the manager and the accountant approve it.' },
      { q: 'Stock did not increase after buying', a: 'Open the purchase and click Confirm — drafts do not change stock.' },
    ],
    sections: [
      { h: 'Add a stock item', d: 'Every item you store.',
        steps: [
          'Open **Stock Catalogue** and click **＋ Add Stock Item**.',
          'Type the **Item Name** (e.g. Rice 25kg bag) and choose the **Category** and **Unit**.',
          { t: 'Type the **Opening Stock**, **Reorder Level** and **Unit Cost (KES)**.', tip: 'Reorder Level = the quantity at which you want to be warned to buy more.' },
          'Type the **Supplier** and save.',
        ],
        result: 'The item appears in the catalogue with its value. LOW STOCK shows when it reaches the reorder level.',
      },
      { h: 'Issue stock to a department', d: 'Kitchen, bar or housekeeping takes items from the store.',
        steps: [
          'Open **Issuances** and click **＋ New Issuance**.',
          'Search and select the items, then type each quantity.',
          'Choose the **Department** (required) and **Requested By**, and type the **Purpose / Notes** (e.g. Morning service).',
          'Complete the issuance.',
        ],
        result: 'Stock is deducted and the issue voucher prints. 🖨 Print reprints it later.',
        mistakes: ['Handing over goods without recording an issuance — stock will not match.'],
      },
      { h: 'Order stock (shopping list)', d: 'Get approval and money to buy.',
        steps: [
          { t: 'Open **Shopping Lists** and click **＋ New Shopping List** — or generate one from low-stock items.' },
          'Add items, quantities and estimated costs (**Add Custom Item** for things not in stock yet). Give it a title and notes.',
          'Save. It goes to the manager.',
          { t: 'Approval flow: **Pending Manager Approval** → **Manager Approved — Awaiting Accountant** → **Fully Approved**.', tip: 'You can print the list only after full approval.' },
          'After buying, click **✓ Mark Purchased**.',
        ],
      },
      { h: 'Receive goods (purchase)', d: 'Put new stock into the system.',
        steps: [
          'Open **Purchases** and click **+ New Purchase**.',
          'Optionally choose **Link to Shopping List**.',
          'Type the **Supplier** (required), **Invoice Number** and **Purchase Date**.',
          'Add each line item with quantity and unit cost. Save — it is a draft.',
          { t: 'When the goods are physically counted in, click **Confirm**.', tip: 'Only confirmed purchases add stock.' },
        ],
        result: '“Purchase confirmed — stock levels have been updated.”',
      },
      { h: 'Correct stock after a count or damage', d: 'Keep the system matching the shelves.',
        steps: [
          'Open **Stock Adjustment** and select the item.',
          'Choose the **Adjustment Type**: 📦 Receive / Purchase, 🔢 Stock Count Correction, 🗑️ Write-Off / Damage, ↩️ Return to Supplier or 📝 Other.',
          'Type the quantity, the **Reason / Notes** and **Reference (LPO/DN No.)**.',
          { t: 'Check **New stock after adjustment** and save.', tip: 'It cannot go below zero and warns if it drops below the reorder level.' },
        ],
      },
      { h: 'Watch low stock and movements', d: 'Never run out.',
        steps: [
          'Open **Low Stock** daily. Click **📞 Flag for Reorder** or raise a shopping list.',
          'Open **Movement Log** for every receipt, issue, write-off and adjustment; export with **⬇ Excel** or **⬇ PDF**.',
          'In Issuances, **📊 Reports & Export** shows value issued by department.',
        ],
      },
    ],
  },

  /* ═════════════════════════ GIFT SHOP ═════════════════════════ */
  main_gift_shop: {
    title: 'Gift Shop',
    intro: 'Sell items, take payment or charge hotel guests’ rooms, and keep stock up to date.',
    faq: [{ q: 'I cannot add more of an item', a: 'You reached the stock on hand. Restock it in Inventory first.' }],
    sections: [
      { h: 'Make a sale', d: 'Sell to a walk-in customer or hotel guest.',
        steps: [
          'Open **Point of Sale**. Use the categories or **🔍 Search…** and tap items to add them to the cart.',
          'Type the **Customer Name (optional)**.',
          { t: 'For a hotel guest, choose them in **Hotel Guest (Room Charge)**.', tip: 'It is posted to their folio and paid at checkout.' },
          'Otherwise choose the **Payment Method** (Cash, M-Pesa, PDQ / Card or Stripe) and type the transaction code.',
          'Submit the sale. **Clear Cart** starts over.',
        ],
        result: 'Room charges print a receipt immediately; other sales go to the Payment Queue.',
      },
      { h: 'Confirm a payment', d: 'Check the money and print the receipt.',
        steps: [
          'Open **Payment Queue** and the sale.',
          'Check the money. If needed, set the correct mode and code under **Override payment details (optional)**.',
          'Confirm. The receipt prints.',
        ],
      },
      { h: 'Add or restock items', d: 'Keep the shelves and system in step.',
        steps: [
          { t: 'Open **Inventory** and click **+ Add Item**: name, category, SKU (left blank = automatic), selling and cost price, initial stock, reorder level, description and an image (max 2 MB).' },
          'Keep **Item is active / available for sale** ticked to sell it.',
          { t: 'When goods arrive, click **+ Restock**, type the **Quantity to Add** and **+ Add Stock**.' },
          'Use **Edit** or **Del**. Low stock items are flagged.',
        ],
      },
      { h: 'Reprint a receipt', d: 'From past sales.',
        steps: ['Open **Sales History**, click **View** on the sale and **🖨 Print Receipt**.'],
      },
    ],
  },

  /* ═════════════════════════ POOL & KIDS ═════════════════════════ */
  swimming: {
    title: 'Pool & Kids Zone',
    intro: 'Record pool entries, sell swim gear and extras, and see payments. Pool hours 6:00 AM – 10:00 PM; hotel guests swim free; children under 5 must be supervised.',
    faq: [{ q: 'A hotel guest was charged', a: 'Link the guest using Link Hotel Guest so entry becomes free (FOC).' }],
    sections: [
      { h: 'Record a pool entry', d: 'Every swimmer, paid or free.',
        steps: [
          'Open **Pool Entry**. Type the **Visitor / Guardian Name**.',
          { t: 'Choose the **Visitor Type**: Adult Resident, Child Resident (<12), Adult Non-Resident, Child Non-Resident or Hotel Guest (FOC).' },
          'Type the **Number of Adults** and **Children (<12 yrs)**.',
          { t: 'For a hotel guest, choose them in **Link Hotel Guest (auto-FOC)** — entry becomes free.' },
          'Otherwise choose the **Payment Method** and type the **Transaction Reference**.',
          'Use **Quick-Add Swim Gear** to add costumes, goggles or towels to the same bill.',
          'Check the **Bill Summary** and save.',
        ],
        result: '“Entry recorded” — the swimmer is counted in Currently In Pool.',
      },
      { h: 'Sell swim gear, kids activities or loungers', d: 'Extras on their own.',
        steps: [
          'Open **Swim Extras** and choose a group: Swim Gear, Kids Zone or Sun Lounging.',
          'Add items to the **Cart**, type the guest/visitor name and choose the payment.',
          'Save. **Clear Extras** empties the cart.',
        ],
      },
      { h: 'Check payments and rates', d: 'Takings and price list.',
        steps: [
          'Open **Payments** and choose **Today**, **This Month** or **All Records**.',
          'Open **Rates & Info** for pool rates, hire rates and pool rules.',
        ],
      },
    ],
  },
  main_swimming_attendant: {
    title: 'Pool Attendant',
    intro: 'Record everyone who swims and see who is in the pool right now.',
    sections: [
      { h: 'Record a swimmer', d: 'Every entry, paid or free.',
        steps: [
          'Open **New Entry** and choose the **Visitor Type**.',
          { t: 'For a hotel guest, choose them in **Select Guest** — entry is free.' },
          'Otherwise type the **Visitor Name**.',
          'Type the **Number of Adults** and **Number of Children (under 12)**.',
          { t: 'Choose the **Payment Mode** and type the **Transaction code**.', tip: 'The code is required for paid entries (M-Pesa, card).' },
          'Check the **Entry Summary** and save.',
        ],
        result: '“Pool entry recorded ✅” — they appear as IN POOL.',
      },
      { h: 'See who is in the pool', d: 'Safety and headcount.',
        steps: ['Open **Active Guests** — everyone currently IN POOL.', 'Open **Today** for all of today’s entries.'],
      },
    ],
  },
  main_kids_amenities: {
    title: 'Kids Zone',
    intro: 'Book kids activities, take payment and see today’s schedule.',
    sections: [
      { h: 'Book activities for a child', d: 'Water slide, trampoline, face painting and more.',
        steps: [
          'Open **New Booking** and tap the activities under **Select Activities**.',
          'Type the **Child’s Name** and **Age**, and the **Parent / Guardian Name**.',
          { t: 'For a hotel guest, choose them in **Hotel Guest (optional)** and pay by Room Charge.' },
          'Otherwise choose the **Payment Mode** and type the **Transaction Reference** (M-Pesa code / receipt no.).',
          'Check the **Booking Summary** and save. Click **🖨 Print** for the ticket.',
        ],
        result: '“Kids booking recorded ✅” with the total.',
      },
      { h: 'Prices, bookings and today’s schedule', d: 'Plan the day.',
        steps: [
          '**Activities Menu** — every activity and its price.',
          '**Bookings Log** — all bookings.',
          '**Today** — scheduled group activities (story time, puppet show…).',
        ],
      },
    ],
  },

  /* ═════════════════════════ DRIVER ═════════════════════════ */
  main_driver: {
    title: 'Driver',
    intro: 'See trips booked by reception, accept them and mark them done.',
    sections: [
      { h: 'Take a trip', d: 'From request to drop-off.',
        steps: [
          'Open **⏳ Pending** to see new trips.',
          { t: 'Read the card: pickup time and place, destination, passengers, vehicle, fee, payment and the guest’s phone, plus notes.', tip: 'Notes often contain the flight number — check arrival times before you leave.' },
          'Tap **✓ Accept Trip**. It moves to **🔵 Active**.',
          'Call the guest if needed and do the trip.',
          'After drop-off, tap **✅ Mark Complete**.',
        ],
        result: '“Trip marked as completed ✅”. It moves to Completed.',
      },
      { h: 'A trip is called off', d: 'Guest cancelled or no-show.',
        steps: ['Open the trip and tap **Cancel Trip**, then confirm.', 'It moves to **⛔ Cancelled**. Tell reception.'],
      },
    ],
  },
};
